import React from 'react';
import { Gift, Heart, Mail, Facebook, Instagram, Twitter, Share2, MessageCircle } from 'lucide-react';

const Footer = ({ setCurrentPage }) => {
  const currentYear = new Date().getFullYear();
  const currentUrl = window.location.href;
  const siteTitle = "Smartgave - Finn den perfekte gaven til enhver anledning";
  const siteDescription = "Personlige gaveanbefalinger for alle anledninger og budsjetter. Norges mest omfattende gaveguide.";

  // Sosiale medier deling URLs
  const socialShareUrls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(siteTitle)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${siteTitle} - ${currentUrl}`)}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(currentUrl)}&description=${encodeURIComponent(siteDescription)}`
  };

  const handleSocialShare = (platform) => {
    const url = socialShareUrls[platform];
    if (url) {
      window.open(url, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
    }
  };

  const footerLinks = {
    gavekategorier: [
      { id: 'wedding', name: 'Bryllupsgaver', icon: '💍' },
      { id: 'birthday', name: 'Bursdagsgaver', icon: '🎂' },
      { id: 'christmas', name: 'Julegaver', icon: '🎄' },
      { id: 'electronics', name: 'Elektronikk', icon: '📱' }
    ],
    mottakere: [
      { id: 'for-him', name: 'Gaver til han', icon: '👨' },
      { id: 'for-her', name: 'Gaver til henne', icon: '👩' },
      { id: 'for-kids', name: 'Gaver til barn', icon: '👶' },
      { id: 'for-teachers', name: 'Gaver til lærere', icon: '👩‍🏫' }
    ],
    prisklasser: [
      { id: 'under-200', name: 'Under 200 kr', icon: '💸' },
      { id: '200-500', name: '200-500 kr', icon: '💰' },
      { id: '500-1000', name: '500-1000 kr', icon: '💎' },
      { id: '1000-plus', name: 'Over 1000 kr', icon: '👑' }
    ]
  };

  return (
    <footer className="bg-secondary-800 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              <Gift className="w-8 h-8 text-primary-400" />
              <span className="text-xl font-bold">Smartgave</span>
            </div>
            <p className="text-secondary-300 mb-4">
              Din guide til den perfekte gaven. Vi hjelper deg å finne gaver som virkelig gleder.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium mb-6"
            >
              <Gift className="w-4 h-4 inline mr-2" />
              Start gavefinneren
            </button>

            {/* Sosiale medier deling */}
            <div>
              <h4 className="text-sm font-semibold mb-3 flex items-center">
                <Share2 className="w-4 h-4 mr-2" />
                Del Smartgave
              </h4>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleSocialShare('facebook')}
                  className="p-2 bg-secondary-700 hover:bg-blue-600 rounded-lg transition-colors group"
                  title="Del på Facebook"
                  aria-label="Del på Facebook"
                >
                  <Facebook className="w-5 h-5 text-secondary-300 group-hover:text-white" />
                </button>
                
                <button
                  onClick={() => handleSocialShare('twitter')}
                  className="p-2 bg-secondary-700 hover:bg-blue-400 rounded-lg transition-colors group"
                  title="Del på Twitter"
                  aria-label="Del på Twitter"
                >
                  <Twitter className="w-5 h-5 text-secondary-300 group-hover:text-white" />
                </button>
                
                <button
                  onClick={() => handleSocialShare('linkedin')}
                  className="p-2 bg-secondary-700 hover:bg-blue-700 rounded-lg transition-colors group"
                  title="Del på LinkedIn"
                  aria-label="Del på LinkedIn"
                >
                  <svg className="w-5 h-5 text-secondary-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </button>
                
                <button
                  onClick={() => handleSocialShare('whatsapp')}
                  className="p-2 bg-secondary-700 hover:bg-green-600 rounded-lg transition-colors group"
                  title="Del på WhatsApp"
                  aria-label="Del på WhatsApp"
                >
                  <MessageCircle className="w-5 h-5 text-secondary-300 group-hover:text-white" />
                </button>
                
                <button
                  onClick={() => handleSocialShare('pinterest')}
                  className="p-2 bg-secondary-700 hover:bg-red-600 rounded-lg transition-colors group"
                  title="Del på Pinterest"
                  aria-label="Del på Pinterest"
                >
                  <svg className="w-5 h-5 text-secondary-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.407-5.965 1.407-5.965s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.097.118.112.222.083.343-.09.378-.293 1.189-.334 1.355-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24c6.624 0 11.99-5.367 11.99-12C24.007 5.367 18.641.001.012.001z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Gavekategorier */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Gavekategorier</h3>
            <ul className="space-y-2">
              {footerLinks.gavekategorier.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => setCurrentPage(link.id)}
                    className="text-secondary-300 hover:text-primary-400 transition-colors text-sm flex items-center space-x-2"
                  >
                    <span>{link.icon}</span>
                    <span>{link.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Mottakere */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Mottakere</h3>
            <ul className="space-y-2">
              {footerLinks.mottakere.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => setCurrentPage(link.id)}
                    className="text-secondary-300 hover:text-primary-400 transition-colors text-sm flex items-center space-x-2"
                  >
                    <span>{link.icon}</span>
                    <span>{link.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Kontakt og Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Kontakt</h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => setCurrentPage('contact')}
                  className="text-secondary-300 hover:text-primary-400 transition-colors text-sm flex items-center space-x-2"
                >
                  <Mail className="w-4 h-4" />
                  <span>Kontakt oss</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setCurrentPage('home')}
                  className="text-secondary-300 hover:text-primary-400 transition-colors text-sm flex items-center space-x-2"
                >
                  <Gift className="w-4 h-4" />
                  <span>Hjem</span>
                </button>
              </li>
            </ul>

            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-2">Prisklasser</h4>
              <div className="grid grid-cols-2 gap-1">
                {footerLinks.prisklasser.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => setCurrentPage(link.id)}
                    className="text-xs text-secondary-400 hover:text-primary-400 transition-colors text-left"
                  >
                    {link.icon} {link.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Følg oss seksjon */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-2 flex items-center">
                <Instagram className="w-4 h-4 mr-2" />
                Følg oss
              </h4>
              <div className="flex space-x-2">
                <a
                  href="https://www.facebook.com/profile.php?id=61577950650988"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary-400 hover:text-blue-400 transition-colors"
                  aria-label="Følg oss på Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href="https://www.instagram.com/smartgave"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary-400 hover:text-pink-400 transition-colors"
                  aria-label="Følg oss på Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://twitter.com/smartgave"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary-400 hover:text-blue-400 transition-colors"
                  aria-label="Følg oss på Twitter"
                >
                  <Twitter className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-secondary-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-secondary-400 text-sm mb-4 md:mb-0">
              © {currentYear} Smartgave. Laget med <Heart className="w-4 h-4 inline text-primary-500" /> for gavegivere.
            </div>
            <div className="flex space-x-6 text-sm">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-secondary-400 hover:text-primary-400 transition-colors"
              >
                Personvern
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="text-secondary-400 hover:text-primary-400 transition-colors"
              >
                Vilkår
              </button>
              <button
                onClick={() => setCurrentPage('contact')}
                className="text-secondary-400 hover:text-primary-400 transition-colors"
              >
                Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;